import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DL_FILTER_EVENT, DL_FILTER_ACTION_ON } from "helpers/constants";
import { BREAKPOINTS_LG } from "helpers/breakpoints";
import { datalayerWrapper } from "lib/analytics/datalayerHelpers";
import { InputWrapper } from "./elements/InputWrapper";
import { TextInput } from "./elements/TextInput";
import { HiddenInput } from "./elements/HiddenInput";
import { Unit } from "./elements/Unit";
import { Select } from "./elements/Select";
import { SelectItem } from "./elements/SelectItem";
import { ErrorText } from "./elements/ErrorText";
import { HelpText } from "./elements/HelpText";

const getValueToSubmit = (val) => (val !== "" ? [val.trim()] : []);

function FilterWithUnits({
    helpText,
    value,
    data,
    filterId,
    format,
    datalayerKey,
    handleChange,
    dataTestId = `${filterId}`
}) {
    const { limits } = format;

    const anchorRefT = useRef(null);
    const hiddenInput = useRef(null);
    const arrayToValue = (val) => (Array.isArray(val) ? val[0] : val) || "";
    const [inputValue, setInputValue] = useState(arrayToValue(value));
    const [focused, setFocus] = useState(false);
    const [invalidInput, setInvalid] = useState(false);
    const [errorText, setErrorText] = useState("Geen geldig nummer");

    const isValidInput = (input) => {
        const newInputValue = typeof input === "string" ? input.trim() : String(input);

        if (newInputValue.length > 0) {
            if (limits && limits.min && newInputValue < limits.min) {
                setErrorText(`Mag niet minder zijn dan ${limits.min}`);
                return false;
            }

            if (limits && limits.max && newInputValue > limits.max) {
                setErrorText(`Mag niet meer dan ${limits.max} zijn`);
                return false;
            }

            setErrorText("Geen geldig nummer");
            const intValue = Number(newInputValue);
            return !Number.isNaN(intValue);
        }

        return true;
    };

    useEffect(() => {
        const newValue = arrayToValue(value);
        setInputValue(arrayToValue(newValue));
        setInvalid(!isValidInput(newValue));
    }, [value]);

    const handleClick = () => {
        if (window.innerWidth < BREAKPOINTS_LG) {
            anchorRefT.current.scrollIntoView(true);
        }
        setFocus(true);
        hiddenInput.current.focus();
    };

    const setFilterValue = (newValue) => {
        if (!isValidInput(newValue)) {
            setInvalid(true);
            return;
        }

        datalayerWrapper({
            event: DL_FILTER_EVENT,
            filter_action: DL_FILTER_ACTION_ON,
            filter_name: datalayerKey,
            filter_value: newValue?.toLowerCase()
        });

        handleChange(filterId, getValueToSubmit(newValue));
    };

    const handleKeyUp = (event) => {
        setInvalid(false);

        if (event.keyCode === 13) {
            hiddenInput.current.blur();
            setFilterValue(inputValue);
            setFocus(false);
        }
    };

    const handleBlur = () => {
        setFilterValue(inputValue);
        setFocus(false);
    };

    const renderDropdown = (items) => {
        return (
            <Select>
                {items.map((item) => (
                    <SelectItem
                        onClick={() => {
                            setInputValue(item.id.toString());
                            setFilterValue(item.id.toString());
                        }}
                        key={item.id}
                        onMouseDown={(e) => {
                            e.preventDefault();
                        }}
                        value={item.id}
                    >
                        {`${item.id} ${format.unit}`}
                    </SelectItem>
                ))}
            </Select>
        );
    };

    return (
        <InputWrapper data-testid={dataTestId}>
            <div ref={anchorRefT} />

            <TextInput
                focused={focused}
                invalidInput={invalidInput}
                onBlur={handleBlur}
                onMouseOver={() => hiddenInput.current.focus()}
                onClick={handleClick}
                onFocus={() => setFocus(true)}
            >
                <HiddenInput
                    key={filterId}
                    ref={hiddenInput}
                    value={inputValue}
                    onKeyUp={handleKeyUp}
                    onClick={() => setFocus(true)}
                    onChange={(e) => setInputValue(e.target.value)}
                />

                {data && focused && !inputValue && renderDropdown(data)}

                <Unit showUnit={focused || inputValue}>{format.unit}</Unit>
            </TextInput>

            <HelpText smallHelpText={inputValue || focused}>{helpText}</HelpText>
            <ErrorText invalidInput={invalidInput}>{errorText}</ErrorText>
        </InputWrapper>
    );
}

FilterWithUnits.propTypes = {
    filterId: PropTypes.string.isRequired,
    handleChange: PropTypes.func.isRequired,
    value: PropTypes.arrayOf(PropTypes.string).isRequired,
    datalayerKey: PropTypes.string.isRequired,
    helpText: PropTypes.string
};

FilterWithUnits.defaultProps = {
    defaultValue: [],
    format: { unit: "" }
};

export default FilterWithUnits;
